import { createFileRoute, notFound } from "@tanstack/react-router";
import { loadBlockPage } from "~/utils/content-fns";
import { BlockRenderer } from "~/components/blocks";
import {
  heroHeading,
  pageSchemaScripts,
  seo,
  seoLinks,
  type PageSchemaFields,
  type PageSchemaType,
} from "~/utils/seo";

export const Route = createFileRoute("/solutions/$slug")({
  loader: async ({ params }) => {
    const page = await loadBlockPage({ data: { collection: "solutions", slug: params.slug } });
    return {
      slug: page.slug,
      title: page.title,
      seo_title: page.seo_title,
      seo_description: page.seo_description,
      featured_image: page.featured_image,
      hero_heading: (page as any).hero_heading as string | undefined,
      schema_type: page.schema_type as PageSchemaType | undefined,
      schema: page.schema as PageSchemaFields | undefined,
      blocks: page.blocks || [],
    };
  },
  head: ({ loaderData }) => ({
    meta: loaderData
      ? seo({
          title: loaderData.seo_title || loaderData.title,
          description: loaderData.seo_description || "",
          url: `/solutions/${loaderData.slug}`,
        })
      : [],
    links: loaderData ? seoLinks({ url: `/solutions/${loaderData.slug}` }) : [],
    scripts: loaderData
      ? pageSchemaScripts({
          schemaType: loaderData.schema_type,
          url: `/solutions/${loaderData.slug}`,
          name: heroHeading(loaderData.blocks),
          title: loaderData.seo_title || loaderData.title,
          description: loaderData.seo_description,
          productName: loaderData.title,
          fields: loaderData.schema,
        })
      : [],
  }),
  component: SolutionPage,
});

function SolutionPage() {
  const page = Route.useLoaderData();
  // If the page has no renderable blocks (legacy non-migrated layouts where
  // `blocks:` is just a list of items without a `block:` discriminator),
  // render at least an H1 so the page isn't blank and SEO crawlers can
  // identify the primary heading.
  const hasRenderableBlocks =
    Array.isArray(page.blocks) &&
    page.blocks.some((b: any) => b && typeof b.block === "string");
  if (!hasRenderableBlocks) {
    return (
      <div className="px-4 py-16 md:px-8 md:py-24">
        <div className="mx-auto max-w-screen-xl">
          <h1 className="font-display text-40 font-bold text-charcoal md:text-49 lg:text-57">
            {page.hero_heading || page.title}
          </h1>
        </div>
      </div>
    );
  }
  return <BlockRenderer blocks={page.blocks} />;
}
